import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from '../util/router';
import { Card, Table, Row, Col, Badge, Button } from 'react-bootstrap';
import './SamplesDashboard.css';

const WORKFLOW_OPTIONS = ['Workflow A', 'Workflow B', 'Workflow C'];
const WORKFLOW_MAP = { 'Workflow A': 'WF-A', 'Workflow B': 'WF-B', 'Workflow C': 'WF-C' };

function seedSamples() {
  const arr = [];
  const wfCombos = [
    ['Workflow A'],
    ['Workflow B'],
    ['Workflow C'],
    ['Workflow A', 'Workflow C'],
    ['Workflow B', 'Workflow C'],
    ['Workflow A', 'Workflow B'],
    []
  ];
  const statuses = [
    { status: 'received', batchid: '2025-Smi-20250919-22944' },
    { status: 'in_progress', batchid: '2025-Acm-20250917-16931' },
    { status: 'on_hold', batchid: '2025-Acm-20250916-17259' },
    { status: 'released', batchid: '2026-Pea-20250920-16273' }
  ];

  for (let i = 1; i <= 24; i++) {
    const wf = wfCombos[i % wfCombos.length];
    const st = statuses[i % statuses.length];
    const overdue = i % 6 === 0;
    const needsApproval = i % 4 === 0;
    const oos = i % 5 === 0;
    const sid = `SMP-2025-${String(i).padStart(6, '0')}`;
    const due = new Date('2025-09-15T17:00:00Z');
    if (overdue) due.setDate(due.getDate() - 3);

    arr.push({
      sample_id: sid,
      location_code: `STA-${String((i % 25) + 1).padStart(3, '0')}`,
      priority: i % 7 === 0 ? 'hot' : i % 3 === 0 ? 'rush' : 'standard',
      status: st.status,
      batchid: st.batchid,
      esig_status: needsApproval
        ? 'pending'
        : st.status === 'released'
          ? 'approved'
          : 'not_required',
      due_datetime: due.toISOString(),
      workflows: wf,
      primary_workflow: wf[0] || '',
      workflows_codes: wf.map((w) => WORKFLOW_MAP[w]).join(','),
      spec_status: oos ? 'fail' : 'pass'
    });
  }
  return arr;
}

function isOverdue(sample) {
  if (!sample.due_datetime) return false;
  const now = new Date();
  return new Date(sample.due_datetime) < now && (sample.status || '') !== 'released';
}

function needsApproval(sample) {
  return (sample.esig_status || '').toLowerCase() === 'pending';
}

function isOutOfSpec(sample) {
  return (sample.spec_status || '').toLowerCase() === 'fail';
}

function needsOtherAction(sample) {
  const onHold = (sample.status || '').toLowerCase() === 'on_hold';
  return onHold;
}

function summarizeForWorkflow(samples, workflow) {
  const subset = samples.filter(
    (s) => Array.isArray(s.workflows) && s.workflows.includes(workflow)
  );
  return {
    workflow,
    code: WORKFLOW_MAP[workflow],
    total: subset.length,
    approvals: subset.filter(needsApproval).length,
    oos: subset.filter(isOutOfSpec).length,
    next: subset.filter(needsOtherAction).length,
    on_hold: subset.filter((s) => (s.status || '') === 'on_hold').length
  };
}

function WorkflowCard({ card }) {
  return (
    <Card className="workflow-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="workflow-title">
            {card.workflow} <small className="text-muted">({card.code})</small>
          </div>
          <Badge bg="light" text="dark" className="total-badge">
            {card.total} total
          </Badge>
        </div>
        <Row className="workflow-stats">
          <Col xs={6}>
            <div className="stat-item">
              <span className="stat-label">Approvals:</span>
              <span className="stat-value">{card.approvals}</span>
            </div>
          </Col>
          <Col xs={6}>
            <div className="stat-item">
              <span className="stat-label">Out of Spec:</span>
              <span className="stat-value">{card.oos}</span>
            </div>
          </Col>
          <Col xs={6}>
            <div className="stat-item">
              <span className="stat-label">Next Actions:</span>
              <span className="stat-value">{card.next}</span>
            </div>
          </Col>
          <Col xs={6}>
            <div className="stat-item">
              <span className="stat-label">On Hold:</span>
              <span className="stat-value">{card.on_hold}</span>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function SamplesTable({ rows, onSampleClick }) {
  const formatWorkflows = (wf) => (Array.isArray(wf) ? wf.join(', ') : wf || '—');
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return dateStr.replace('T', ' ').replace('Z', '').substring(0, 16);
  };

  const getPriorityVariant = (priority) => {
    switch (priority) {
      case 'hot':
        return 'danger';
      case 'rush':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'fail':
        return 'danger';
      case 'review':
        return 'warning';
      default:
        return 'success';
    }
  };

  return (
    <Table hover responsive className="samples-table">
      <thead>
        <tr>
          <th>Sample</th>
          <th>Location</th>
          <th>Priority</th>
          <th>Workflows</th>
          <th>Status</th>
          <th>Batch ID</th>
          <th>Due</th>
          <th>Spec</th>
          <th>eSig</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr
            key={r.sample_id}
            onClick={() => onSampleClick(r.sample_id)}
            style={{ cursor: 'pointer' }}
          >
            <td className="fw-bold">{r.sample_id}</td>
            <td>{r.location_code}</td>
            <td>
              <Badge bg={getPriorityVariant(r.priority)}>{r.priority}</Badge>
            </td>
            <td>{formatWorkflows(r.workflows)}</td>
            <td>{r.status}</td>
            <td>{r.batchid || '—'}</td>
            <td>{formatDate(r.due_datetime)}</td>
            <td>
              {r.spec_status && <Badge bg={getStatusVariant(r.spec_status)}>{r.spec_status}</Badge>}
            </td>
            <td>{r.esig_status || '—'}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export default function SamplesDashboard() {
  const [samples] = useState(() => seedSamples());
  const history = useHistory();

  const cards = useMemo(
    () => WORKFLOW_OPTIONS.map((w) => summarizeForWorkflow(samples, w)),
    [samples]
  );
  const approvals = useMemo(() => samples.filter(needsApproval), [samples]);
  const oos = useMemo(() => samples.filter(isOutOfSpec), [samples]);
  const next = useMemo(() => samples.filter(needsOtherAction), [samples]);

  const handleSampleClick = (sampleId) => {
    history.push(`/samples/${sampleId}`);
  };

  return (
    <div className="samples-dashboard">
      <div className="dashboard-header mb-4">
        <h1>Samples Dashboard</h1>
        <p className="text-muted">Workflow-first overview with actionable queues.</p>
      </div>

      <Card className="mb-4 workflow-overview-card">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Workflow Overview</h5>
            <div>
              <Button variant="outline-secondary" size="sm" className="me-1">
                Guide
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-1">
                History
              </Button>
              <Button variant="outline-secondary" size="sm">
                Docs
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            {cards.map((c) => (
              <Col key={c.workflow} lg={4} md={6} className="mb-3">
                <WorkflowCard card={c} />
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Samples Needing Approval (e-Signature Pending)</h5>
            <div>
              <Button variant="outline-secondary" size="sm" className="me-1">
                Guide
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-1">
                History
              </Button>
              <Button variant="outline-secondary" size="sm">
                Docs
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <SamplesTable rows={approvals.slice(0, 12)} onSampleClick={handleSampleClick} />
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Samples Needing Attention (Out of Spec)</h5>
            <div>
              <Button variant="outline-secondary" size="sm" className="me-1">
                Guide
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-1">
                History
              </Button>
              <Button variant="outline-secondary" size="sm">
                Docs
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <SamplesTable rows={oos.slice(0, 12)} onSampleClick={handleSampleClick} />
        </Card.Body>
      </Card>

      <Card className="mb-4 other-actions-card">
        <Card.Header className="bg-warning bg-opacity-10">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Samples Needing Other Actions (Holds, Overdue, Info Needed)</h5>
            <div>
              <Button variant="outline-secondary" size="sm" className="me-1">
                Guide
              </Button>
              <Button variant="outline-secondary" size="sm" className="me-1">
                History
              </Button>
              <Button variant="outline-secondary" size="sm">
                Docs
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <SamplesTable rows={next.slice(0, 12)} onSampleClick={handleSampleClick} />
        </Card.Body>
      </Card>
    </div>
  );
}
